import './global/utils/quillSetup';
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App.jsx';
import { initStore } from './config/store';
import { Provider } from 'react-redux';
import { BrowserRouter } from "react-router-dom";
import * as serviceWorker from './serviceWorker';
import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { setupSentryGlobalHandlers } from './global/utils/setupSentryGlobalHandlers';
import { setupAuthInterceptor } from './global/utils/authInterceptor';
import { isIncludesPath } from "./global/utils/isIncludesUtils";
import SentryBackendReportModal from "./global/components/modals/sentryBackendReportModal";
import getLocalStoreUser from "./global/utils/getLocalStoreUser";

const env_val = process.env.REACT_APP_ENV || 'staging';
const withSentry = !['localdev2', 'localdev3', 'localdev4'].includes(env_val);
const publicPaths = [
    '/', '/user/login', '/user/register', '/user/register/*', '/unauthorized',
    '/user/forgot-password', '/user/reset-password/:hex', '/user/register-error',
    '/user/register-success', '/user/email-verification/:hex',
    '/user/forgot-password-success', '/user/forgot-password-error',
    '/NewLogoPQLarge.png', '/newFavicon.png',
];

if (withSentry) {
    Sentry.init({
        dsn: "https://1ab38b307c0d3a43a832cc85e7a4bde8@o4509525514846213.ingest.us.sentry.io/4509525575925760",
        sendDefaultPii: true,
        integrations: [browserTracingIntegration],
        environment: env_val,
        serverName: env_val,
        tracesSampleRate: 1.0,
    });
    setupSentryGlobalHandlers();
}

let loggedInUser;
try {
    loggedInUser = window.currentUser;
} catch (error) {
    loggedInUser = null;
}
delete window.currentUser;

const loc_user = localStorage.getItem('user');
const store_user = loc_user && loc_user !== 'undefined' ? JSON.parse(loc_user) : null;
const store = initStore(store_user);

if (loggedInUser && withSentry) {
    Sentry.setUser({ id: loggedInUser?.id || '' });
} else if (store_user && withSentry) {
    Sentry.setUser({ id: store_user?.id || '' });
}

function Root() {
    const [reportEventId, setReportEventId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAuthChecked, setIsAuthChecked] = useState(false);

    const handleSentryReport = useCallback((eventId) => {
        if (withSentry) {
            setReportEventId(eventId);
            setIsModalOpen(true);
        }
    }, []);

    useEffect(() => {
        setupAuthInterceptor(handleSentryReport);
    }, [handleSentryReport]);

    const getHomePath = (user) => {
        if (!user) return '/user/login';
        if (user.admin) return "/admin";
        if (user.instructor) return "/instructor";
        return "/learn";
    };

    const forceSyncLogout = () => {
        localStorage.removeItem('user');
        window.location.href = '/user/login';
    };

    useEffect(() => {
        async function checkAuthAndInit() {
            if (isIncludesPath(window.location.pathname, publicPaths)) {
                setIsAuthChecked(true);
                return;
            }
            try {
                const response = await fetch(`/api/me`, { credentials: 'include' });
                if (!response.ok) {
                    forceSyncLogout();
                    return;
                }
            } catch (error) {
                forceSyncLogout();
                return;
            }
            setIsAuthChecked(true);
        }
        checkAuthAndInit();
    }, []);

    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === 'user') {
                if (!event.newValue) {
                    window.location.href = '/user/login';
                } else {
                    const newUser = JSON.parse(event.newValue);
                    window.location.href = getHomePath(newUser);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                if (isIncludesPath(window.location.pathname, publicPaths)) return;

                try {
                    const response = await fetch(`/api/me`, { credentials: 'include' });
                    if (!response.ok) {
                        forceSyncLogout();
                        return;
                    }
                    const serverUser = await response.json();
                    const localUser = getLocalStoreUser();

                    if (serverUser && localUser && serverUser.id !== localUser.id) {
                        localStorage.setItem('user', JSON.stringify(serverUser));
                        window.location.href = getHomePath(serverUser);
                    }
                } catch (error) {
                    forceSyncLogout();
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    const getLocalUserName = () => {
        const user = getLocalStoreUser();
        let name = user?.firstName || '';
        if (user?.lastName) name += (name ? ' ' : '') + user.lastName;
        return name;
    };

    if (!isAuthChecked) return <div className="loading-screen">Loading...</div>;

    return (
        <>
            <BrowserRouter>
                <App store={store} initialUser={loggedInUser} />
            </BrowserRouter>
            <SentryBackendReportModal
                eventId={reportEventId}
                isOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                userName={getLocalUserName()}
                userEmail={getLocalStoreUser()?.username || ''}
            />
        </>
    );
}

ReactDOM.render(
    <React.StrictMode>
        <Provider store={store}><Root /></Provider>
    </React.StrictMode>,
    document.getElementById('root')
);

serviceWorker.unregister();